import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import Select from "react-select";
import useDarkMode from "../../hooks/useDarkMode";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import useThemeColor from "../../hooks/useThemeColor";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import "./TableSpentReport.css";
import {
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";

const normalizeCurrencyCode = (currencyCode) => {
  const map = {
    "£": "GBP",
    "€": "EUR",
    "€ ": "EUR",
    $: "USD",
    "₹": "INR",
    "₨": "PKR",
    Rs: "PKR",
  };

  if (!currencyCode) return "GBP";
  const trimmed = currencyCode.trim();
  if (map[trimmed]) return map[trimmed];
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  return "GBP";
};

const currencyFormatter = (currencyCode) => {
  const safeCode = normalizeCurrencyCode(currencyCode);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: safeCode,
    minimumFractionDigits: 2,
  });
};

const formatNumber = (value) => new Intl.NumberFormat().format(value ?? 0);

const TABLE_SPENT_SORT_FIELDS = {
  tableName: "tableName",
  serviceArea: "serviceAreaName",
  seats: "seats",
  totalCovers: "totalCovers",
  orderCount: "orderCount",
  totalAmount: "totalAmount",
};

const TABLE_SPENT_SORT_LABELS = {
  [TABLE_SPENT_SORT_FIELDS.tableName]: "Table",
  [TABLE_SPENT_SORT_FIELDS.serviceArea]: "Service Area",
  [TABLE_SPENT_SORT_FIELDS.seats]: "Seats",
  [TABLE_SPENT_SORT_FIELDS.totalCovers]: "Covers",
  [TABLE_SPENT_SORT_FIELDS.orderCount]: "Orders",
  [TABLE_SPENT_SORT_FIELDS.totalAmount]: "Total Amount",
};

const TIME_BUCKET_SORT_FIELDS = {
  intervalStart: "intervalStart",
  totalCovers: "totalCovers",
  tableCount: "tableCount",
  totalAmount: "totalAmount",
};

const TIME_BUCKET_SORT_LABELS = {
  [TIME_BUCKET_SORT_FIELDS.intervalStart]: "Time",
  [TIME_BUCKET_SORT_FIELDS.totalCovers]: "Covers",
  [TIME_BUCKET_SORT_FIELDS.tableCount]: "Tables",
  [TIME_BUCKET_SORT_FIELDS.totalAmount]: "Total Amount",
};

const getSortValue = (row, field) => {
  const val = row[field] ?? row[field.charAt(0).toUpperCase() + field.slice(1)];
  if (field === "intervalStart" || field === "tableName") {
    return (val ?? "").toString().toLowerCase();
  }
  return Number(val) || 0;
};

const TableSpentReport = () => {
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = normalizeCurrencyCode(getCompCurrency());
  const currency = useMemo(() => currencyFormatter(compCurrency), [compCurrency]);

  const pageKey = "tableSpentReport";
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: TABLE_SPENT_SORT_FIELDS.totalAmount, label: "Total Amount" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const [serviceAreas, setServiceAreas] = useState([
    { value: "all", label: "All Service Areas" },
  ]);
  const [selectedArea, setSelectedArea] = useState(serviceAreas[0]);
  const [interval, setInterval] = useState({
    value: 60,
    label: "Every 60 min",
  });
  const [rangeDates, setRangeDates] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [summary, setSummary] = useState(null);
  const [timeBuckets, setTimeBuckets] = useState([]);
  const [tableDetails, setTableDetails] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const reportFetchSeqRef = useRef(0);

  const viewModeOptions = [
    { value: "table", label: "Table Base" },
    { value: "time", label: "Time Based" },
  ];

  const intervalOptions = [
    { value: 15, label: "Every 15 min" },
    { value: 30, label: "Every 30 min" },
    { value: 60, label: "Every 60 min" },
    { value: 90, label: "Every 90 min" },
  ];

  const selectedViewMode =
    viewModeOptions.find((option) => option.value === viewMode) ||
    viewModeOptions[0];

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const activeSortLabels =
    viewMode === "table" ? TABLE_SPENT_SORT_LABELS : TIME_BUCKET_SORT_LABELS;

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: activeSortLabels[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value, activeSortLabels]
  );

  const clearFilters = () => {
    setRangeDates([
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ]);
    setSelectedArea(serviceAreas[0]);
    setInterval({ value: 60, label: "Every 60 min" });
  };

  const hasFiltersApplied = () => {
    const todayStart = moment().startOf("day");
    const todayEnd = moment().endOf("day");
    const rangeChanged =
      !moment(rangeDates[0]).isSame(todayStart, "day") ||
      !moment(rangeDates[1] || rangeDates[0]).isSame(todayEnd, "day");
    return (
      rangeChanged ||
      selectedArea?.value !== "all" ||
      interval?.value !== 60
    );
  };

  const fetchServiceAreas = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_SERVICE_AREAS, {
        params: { CompID, Search: "" },
      });
      const raw = response.data?.data || response.data || [];
      const mapped = Array.isArray(raw)
        ? raw.map((area) => ({
            value:
              area.id ?? area.ID ?? area.sA_ID ?? area.sa_ID ?? area.SA_ID,
            label: area.sA_Name ?? area.SA_Name ?? area.name ?? "Service Area",
          }))
        : [];
      const unique = mapped.filter((option) => option.value);
      const options = [{ value: "all", label: "All Service Areas" }, ...unique];
      setServiceAreas(options);
      setSelectedArea((prev) =>
        options.find((opt) => opt.value === prev?.value) || options[0]
      );
    } catch (err) {
      console.error("Failed to load service areas", err);
    }
  }, [CompID]);

  const fetchReport = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++reportFetchSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const startDate = rangeDates[0];
      const endDate = rangeDates[1] || rangeDates[0];

      const params = {
        CompID,
        IntervalMinutes: interval.value,
      };

      if (startDate) {
        params.StartDate = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        params.EndDate = moment(endDate).format("YYYY-MM-DD");
      }
      if (selectedArea?.value && selectedArea.value !== "all") {
        params.ServiceAreaID = selectedArea.value;
      }

      const response = await axiosInstance.get(APIs.GET_TABLE_SPENT_REPORT, { params });

      if (fetchSeq !== reportFetchSeqRef.current) return;

      if (response.data?.status) {
        const payload = response.data.data || response.data || {};
        setSummary(payload.summary || payload.Summary || null);
        setTimeBuckets(payload.timeBuckets || payload.TimeBuckets || []);
        const tables = payload.tableDetails || payload.TableDetails || [];
        const normalized = Array.isArray(tables)
          ? tables.map((table) => ({
              ...table,
              tableName: table.tableName || table.TableName,
              serviceAreaName: table.serviceAreaName || table.ServiceAreaName,
              seats: table.seats ?? table.Seats,
              totalCovers: table.totalCovers ?? table.TotalCovers,
              orderCount: table.orderCount ?? table.OrderCount,
              totalAmount: table.totalAmount ?? table.TotalAmount,
              tableId: table.tableId || table.TableId || table.t_ID,
              Orders: table.orders || table.Orders || [],
            }))
          : [];
        setTableDetails(normalized);
      } else {
        setSummary(null);
        setTimeBuckets([]);
        setTableDetails([]);
        setError(
          response.data?.message || "Unable to fetch table spent report."
        );
      }
    } catch (err) {
      if (fetchSeq !== reportFetchSeqRef.current) return;
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Unable to fetch table spent report."
      );
      setSummary(null);
      setTimeBuckets([]);
      setTableDetails([]);
    } finally {
      if (fetchSeq === reportFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, interval.value, rangeDates, selectedArea]);

  useEffect(() => {
    fetchServiceAreas();
  }, [fetchServiceAreas]);

  useDebouncedEffect(() => fetchReport(), [fetchReport]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDates, selectedArea?.value, interval?.value]);

  useEffect(() => {
    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useEffect(() => {
    setExpandedRowKey(null);
    setPageNumber(1);
    const defaultField =
      viewMode === "table"
        ? TABLE_SPENT_SORT_FIELDS.totalAmount
        : TIME_BUCKET_SORT_FIELDS.intervalStart;
    dispatch(
      setSortBy({
        pageKey,
        sortBy: {
          value: defaultField,
          label: activeSortLabels[defaultField],
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  useResetPageOnFilterChange(setPageNumber, [
    rangeDates,
    selectedArea?.value,
    interval?.value,
    pageSize?.value,
    sortBy?.value,
    sortOrder?.value,
    viewMode,
  ]);

  const sortedRows = useMemo(() => {
    const source = viewMode === "table" ? tableDetails : timeBuckets;
    const rows = [...source];
    const field = sortBy?.value;
    const dir = sortOrder?.value === "ASC" ? 1 : -1;
    if (!field) return rows;
    return rows.sort((a, b) => {
      const av = getSortValue(a, field);
      const bv = getSortValue(b, field);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [tableDetails, timeBuckets, viewMode, sortBy?.value, sortOrder?.value]);

  const totalRecords = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedRows = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return sortedRows.slice(start, start + size);
  }, [sortedRows, pageNumber, pageSize]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleRangeChange = (dates) => {
    const [start, end] = dates || [];
    if (!start) return;
    setRangeDates([start, end || start]);
  };

  const renderSummary = () => {
    if (!summary) {
      return null;
    }

    const cards = [
      {
        icon: "fa-table",
        label: "Total Tables",
        value: formatNumber(summary.totalTables),
        sub: `Active: ${formatNumber(summary.activeTables)}`,
      },
      {
        icon: "fa-chair",
        label: "Total Covers Capacity",
        value: formatNumber(summary.totalCoversCapacity),
      },
      {
        icon: "fa-users",
        label: "Total Covers Spent",
        value: formatNumber(summary.totalCoversSpent),
      },
      {
        icon: "fa-coins",
        label: "Total Amount Spent",
        value: currency.format(summary.totalAmountSpent || 0),
      },
      {
        icon: "fa-divide",
        label: "Avg Spend / Cover",
        value: currency.format(summary.averageSpendPerCover || 0),
      },
    ];

    return (
      <div
        className="mb-4 px-3 pt-3"
        style={{ position: "relative", zIndex: 0, isolation: "isolate" }}
      >
        <div className="d-flex gap-3 flex-wrap">
          {cards.map((card) => (
            <div
              key={card.label}
              className="flex-grow-1"
              style={{
                minWidth: "170px",
                backgroundColor: themeColor,
                borderRadius: "8px",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "white",
              }}
            >
              <i
                className={`fa ${card.icon}`}
                style={{ fontSize: "22px", opacity: 0.9 }}
              />
              <div>
                <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                  {card.value}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.85 }}>
                  {card.label}
                </div>
                {card.sub && (
                  <div style={{ fontSize: "11px", opacity: 0.75 }}>{card.sub}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOrderCards = (orders, variant) => {
    if (!orders?.length) return null;
    const gridClass =
      variant === "time" ? "orders-grid-time" : "orders-grid-table";
    const cardClass = variant === "time" ? "order-card-time" : "order-card-table";
    const rowClass =
      variant === "time" ? "order-card-time-row" : "order-card-table-row";
    const colClass =
      variant === "time" ? "order-card-time-col" : "order-card-table-col";
    const labelClass =
      variant === "time" ? "order-card-time-label" : "order-card-table-label";
    const valueClass =
      variant === "time" ? "order-card-time-value" : "order-card-table-value";
    const amountClass =
      variant === "time" ? "order-amount-time" : "order-amount-table";
    const badgeClass =
      variant === "time" ? "covers-badge" : "covers-badge-table";

    return (
      <div className={`${variant === "time" ? "time-orders-section" : "table-orders-section"} mt-2`}>
        <div className="orders-header">
          <small className="text-muted fw-semibold">
            <i className="fa fa-receipt me-1" />
            Orders ({orders.length})
          </small>
        </div>
        <div className={gridClass}>
          {orders.map((order, idx) => (
            <div
              key={`${order.orderNumber || order.OrderNumber}-${idx}`}
              className={cardClass}
            >
              <div className={rowClass}>
                <div className={colClass}>
                  <div className={labelClass}>Order #</div>
                  <div className={valueClass}>
                    {(order.orderNumber || order.OrderNumber || "N/A").substring(0, 15)}
                    {(order.orderNumber || order.OrderNumber || "").length > 15
                      ? "..."
                      : ""}
                  </div>
                </div>
                {variant === "table" ? (
                  <div className={colClass}>
                    <div className={labelClass}>
                      <i className="fa fa-clock me-1" />
                      Time
                    </div>
                    <div className={valueClass}>
                      {order.startTime || order.StartTime
                        ? moment(order.startTime || order.StartTime).format("MMM DD")
                        : "N/A"}
                      <br />
                      <small className="text-muted">
                        {order.startTime || order.StartTime
                          ? moment(order.startTime || order.StartTime).format("hh:mm A")
                          : ""}
                      </small>
                    </div>
                  </div>
                ) : (
                  <div className={colClass}>
                    <div className={labelClass}>
                      <i className="fa fa-table me-1" />
                      Table
                    </div>
                    <div className={valueClass}>
                      {order.tableName || order.TableName || "N/A"}
                    </div>
                  </div>
                )}
                <div className={colClass}>
                  <div className={labelClass}>
                    <i className="fa fa-users me-1" />
                    Covers
                  </div>
                  <div className={valueClass}>
                    <span className={badgeClass}>
                      {formatNumber(order.covers || order.Covers || 0)}
                    </span>
                  </div>
                </div>
                <div className={colClass}>
                  <div className={labelClass}>
                    <i className="fa fa-money-bill-wave me-1" />
                    Amount
                  </div>
                  <div className={`${valueClass} ${amountClass}`}>
                    {currency.format(order.amount || order.Amount || 0)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTableGrid = () => {
    const sortFields = TABLE_SPENT_SORT_FIELDS;
    const colSpan = 7;

    return (
      <div
        className="table-responsive"
        style={{ minHeight: "200px", maxHeight: "450px", overflow: "auto" }}
      >
        <table
          className="table table-hover"
          style={{ "--modern-grid-sort-active": themeColor }}
        >
          <thead
            className="bg-light"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
              color: isDarkMode ? "#ffffff" : "#000000",
            }}
          >
            <tr>
              <th className="pt-2 pb-2" style={{ width: 40 }} />
              <SortableColumnHeader
                sortField={sortFields.tableName}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
              >
                Table
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.serviceArea}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
              >
                Service Area
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.seats}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-center"
              >
                Seats
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.totalCovers}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-center"
              >
                Covers
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.orderCount}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-center"
              >
                Orders
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.totalAmount}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-end"
              >
                Total Amount
              </SortableColumnHeader>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((table) => {
              const rowKey = String(table.tableId ?? table.tableName);
              const isExpanded = expandedRowKey === rowKey;
              const orders = table.Orders || table.orders || [];
              return (
                <React.Fragment key={rowKey}>
                  <tr>
                    <td className="align-middle">
                      {orders.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() =>
                            setExpandedRowKey(isExpanded ? null : rowKey)
                          }
                          aria-label={isExpanded ? "Collapse orders" : "Expand orders"}
                        >
                          <i className={`fa fa-chevron-${isExpanded ? "down" : "right"}`} />
                        </button>
                      )}
                    </td>
                    <td className="align-middle fw-semibold">{table.tableName}</td>
                    <td className="align-middle">{table.serviceAreaName || "—"}</td>
                    <td className="align-middle text-center">
                      {formatNumber(table.seats)}
                    </td>
                    <td className="align-middle text-center">
                      {formatNumber(table.totalCovers)}
                    </td>
                    <td className="align-middle text-center">
                      {formatNumber(table.orderCount)}
                    </td>
                    <td className="align-middle text-end fw-semibold">
                      {currency.format(table.totalAmount || 0)}
                    </td>
                  </tr>
                  {isExpanded && orders.length > 0 && (
                    <tr>
                      <td colSpan={colSpan} className="bg-light bg-opacity-50">
                        {renderOrderCards(orders, "table")}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTimeGrid = () => {
    const sortFields = TIME_BUCKET_SORT_FIELDS;
    const colSpan = 5;

    return (
      <div
        className="table-responsive"
        style={{ minHeight: "200px", maxHeight: "450px", overflow: "auto" }}
      >
        <table
          className="table table-hover"
          style={{ "--modern-grid-sort-active": themeColor }}
        >
          <thead
            className="bg-light"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
              color: isDarkMode ? "#ffffff" : "#000000",
            }}
          >
            <tr>
              <th className="pt-2 pb-2" style={{ width: 40 }} />
              <SortableColumnHeader
                sortField={sortFields.intervalStart}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
              >
                Date & Time
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.totalCovers}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-center"
              >
                Covers
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.tableCount}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-center"
              >
                Tables
              </SortableColumnHeader>
              <SortableColumnHeader
                sortField={sortFields.totalAmount}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                className="text-end"
              >
                Total Amount
              </SortableColumnHeader>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((bucket, index) => {
              const rowKey = `${bucket.intervalStart}-${index}`;
              const isExpanded = expandedRowKey === rowKey;
              const orders = bucket.orders || bucket.Orders || [];
              return (
                <React.Fragment key={rowKey}>
                  <tr>
                    <td className="align-middle">
                      {orders.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() =>
                            setExpandedRowKey(isExpanded ? null : rowKey)
                          }
                        >
                          <i className={`fa fa-chevron-${isExpanded ? "down" : "right"}`} />
                        </button>
                      )}
                    </td>
                    <td className="align-middle">
                      {moment(bucket.intervalStart).format("MMM DD, YYYY · hh:mm A")}
                    </td>
                    <td className="align-middle text-center">
                      {formatNumber(bucket.totalCovers)}
                    </td>
                    <td className="align-middle text-center">
                      {formatNumber(bucket.tableCount)}
                    </td>
                    <td className="align-middle text-end fw-semibold">
                      {currency.format(bucket.totalAmount || 0)}
                    </td>
                  </tr>
                  {isExpanded && orders.length > 0 && (
                    <tr>
                      <td colSpan={colSpan} className="bg-light bg-opacity-50">
                        {renderOrderCards(orders, "time")}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const listEmpty =
    initialLoadComplete &&
    !loading &&
    (viewMode === "table" ? tableDetails.length === 0 : timeBuckets.length === 0);

  const getEmptyStateDescription = () => {
    if (hasFiltersApplied()) {
      return "No data matches the selected date range, service area, or interval. Try adjusting filters or clear them.";
    }
    return "No table spent data is available for the selected period.";
  };

  return (
    <div className={`table-spent-page ${isDarkMode ? "dark-mode" : ""}`}>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <a href="#/">Home</a>
        </li>
        <li className="breadcrumb-item">
          <a href="#/report">Reports</a>
        </li>
        <li className="breadcrumb-item active">Table Spent Report</li>
      </ol>
      <h1 className="page-header">Table Spent Report</h1>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!initialLoadComplete && (
        <GridSectionLoader loading minHeight="320px" message="Fetching table spent report..." />
      )}

      {initialLoadComplete && (
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title={viewMode === "table" ? "Tables" : "Time Buckets"}
          totalCount={totalRecords}
          bodyFlush
          topFilters={
            <>
              <ModernGridToolbarFilterPill
                iconClass="fa fa-th-large"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={selectedViewMode}
                  onChange={(option) => setViewMode(option.value)}
                  options={viewModeOptions}
                  styles={filterPillSelectStyles}
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill
                iconClass="fa fa-table"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={selectedArea}
                  onChange={(option) => setSelectedArea(option)}
                  options={serviceAreas}
                  styles={filterPillSelectStyles}
                  placeholder="All Service Areas"
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill
                iconClass="fa fa-clock"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={interval}
                  onChange={(option) => setInterval(option)}
                  options={intervalOptions}
                  styles={filterPillSelectStyles}
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                <DateRangePicker
                  startDate={rangeDates[0]}
                  endDate={rangeDates[1]}
                  onChange={handleRangeChange}
                  placeholder="Select Date Range"
                />
              </ModernGridToolbarFilterPill>
            </>
          }
        >
          {renderSummary()}

          <div className="px-3 pb-3">
            <GridSectionLoader
              loading={loading}
              minHeight="280px"
              message="Fetching table spent report..."
            >
              {listEmpty ? (
                <GridEmptyState
                  isDarkMode={isDarkMode}
                  themeColor={themeColor}
                  title="No data found"
                  description={getEmptyStateDescription()}
                  onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
                  clearFiltersLabel="Clear all filters"
                />
              ) : (
                <>
                  {viewMode === "table" ? renderTableGrid() : renderTimeGrid()}
                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      )}
    </div>
  );
};

export default TableSpentReport;
